import { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "@helpers/axios/private.axios";
import { USER, ALERT } from "@constants";
import { useTranslation } from "@helpers/translate";

export const useAvatarUpload = (reduxAvatarUrl) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localAvatar, setLocalAvatar] = useState(() => {
    return localStorage.getItem("user_avatar") || null;
  });
  const MAX_AVATAR_SIZE_MB = 2;
  const MAX_AVATAR_SIZE_BYTES = MAX_AVATAR_SIZE_MB * 1024 * 1024;

  const getFileExt = (file) => {
    const name = String(file?.name || "").toLowerCase();
    return name.includes(".") ? name.split(".").pop() : "";
  };

  const isWebpFile = (file) => {
    const type = String(file?.type || "").toLowerCase();
    if (type === "image/webp" || type === "image/x-webp") return true;
    return getFileExt(file) === "webp";
  };

  // Convert any image file to a PNG data URL (safe fallback when backend rejects WebP).
  const convertImageFileToPngDataUrl = (inputFile) => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(inputFile);
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;

          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas not supported");

          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          URL.revokeObjectURL(objectUrl);
          resolve(dataUrl);
        } catch (e) {
          URL.revokeObjectURL(objectUrl);
          reject(e);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to decode image"));
      };
      img.src = objectUrl;
    });
  };

  useEffect(() => {
    setLocalAvatar(reduxAvatarUrl);
  }, [reduxAvatarUrl]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    // Some devices/browsers can return empty `file.type` (notably for WebP). Fall back to extension.
    const type = String(file.type || "").toLowerCase();
    const ext = getFileExt(file);
    const isAllowedByExt = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

    if (!(type && allowedTypes.includes(type)) && !(!type && isAllowedByExt)) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("error.invalid_file_type_detailed") ||
          "Please use allowed formats: PNG, JPG, GIF, WebP",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload:
          __("error.file_too_large") ||
          `Image is too large. Please upload a smaller file (up to ${MAX_AVATAR_SIZE_MB}MB).`,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setUploading(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        let base64String = reader.result;

        try {
          // Backend rejects WebP in practice (422). Convert WebP to PNG before sending so user can upload WebP.
          if (isWebpFile(file)) {
            base64String = await convertImageFileToPngDataUrl(file);
          }

          const response = await axios.post("/users/update-avatar", {
            avatar: base64String,
          });

          if (response.status === 200 && response.data.avatar_url) {
            const newAvatarUrl = response.data.avatar_url;

            setLocalAvatar(newAvatarUrl);

            dispatch({
              type: USER.USER_FIELD,
              payload: { avatar_url: newAvatarUrl },
            });

            dispatch({
              type: ALERT.ALERT_SUCCESS,
              payload: __("common.successful") || "Avatar updated",
            });
          } else {
            throw new Error(
              response.data.message || "Failed to update avatar on server",
            );
          }
        } catch (apiError) {
          console.error("API Error during avatar upload:", apiError);

          const status = apiError?.response?.status;
          const hasResponse = apiError?.response !== undefined;

          let errorMessage =
            __("error.upload_failed") || "Failed to upload avatar";

          if (status === 413 || !hasResponse) {
            errorMessage =
              __("error.file_too_large") ||
              `Image is too large. Please upload a smaller file (up to ${MAX_AVATAR_SIZE_MB}MB).`;
          }

          dispatch({
            type: ALERT.ALERT_ERROR,
            payload: errorMessage,
          });
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        dispatch({
          type: ALERT.ALERT_ERROR,
          payload: __("error.upload_failed") || "Failed to read file",
        });
        setUploading(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("File handling error:", error);
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: __("error.upload_failed") || "An unexpected error occurred",
      });
      setUploading(false);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return {
    localAvatar,
    uploading,
    fileInputRef,
    handleUploadClick,
    handleFileChange,
  };
};
